import type { TourDefinition } from './types';

/**
 * Onboarding tour shown to new users immediately after signup.
 * Guides them through household setup and core features.
 */
export const ONBOARDING_TOUR: TourDefinition = {
  id: 'onboarding',
  name: 'Welcome Tour',
  description: 'Set up your household and learn the basics',
  version: 3,
  deviceTypes: ['web', 'mobile'],
  triggerCondition: 'immediate',
  steps: {
    web: [
      {
        id: 'sidebar-nav',
        target: '[data-tour="sidebar-nav"]',
        title: 'Navigation',
        content:
          "Here's your navigation. Each section has its own mini-tour for when you're ready to explore.",
        placement: 'right',
      },
      {
        id: 'lets-get-started',
        target: '[data-tour="sidebar-nav"]',
        title: "Let's Get Started",
        content: "We'll guide you through the key features to get you up and running.",
        placement: 'right',
        handoffTo: 'households',
      },
    ],
    mobile: [
      {
        id: 'welcome',
        target: 'welcome-screen',
        title: 'Welcome to TrackMyGroceries',
        content: "Let's get you set up in about 30 seconds.",
        placement: 'center',
      },
      {
        id: 'tab-overview',
        target: 'tab-bar',
        title: 'Your Main Sections',
        content:
          'Use these tabs to navigate between your dashboard, pantry, meal prep, and more. Next up: setting up your household.',
        placement: 'top',
        handoffTo: 'households',
      },
    ],
  },
};

/**
 * Receipt scanning tour shown when user accesses the receipt scanning feature.
 * Only available to premium users.
 */
export const RECEIPT_SCANNING_TOUR: TourDefinition = {
  id: 'receipt-scanning',
  name: 'Receipt Scanning Tour',
  description: 'Learn how to quickly add items by scanning receipts',
  version: 1,
  requiredTier: 'premium',
  deviceTypes: ['web', 'mobile'],
  triggerCondition: 'on_route',
  triggerRoute: '/receipts',
  steps: {
    web: [
      {
        id: 'upload-receipt',
        target: '[data-tour="receipt-upload"]',
        title: 'Upload a Receipt',
        content:
          "Drag and drop a receipt image or click to upload. We'll extract the items automatically.",
        placement: 'bottom',
      },
      {
        id: 'review-items',
        target: '[data-tour="receipt-items"]',
        title: 'Review & Confirm',
        content:
          'Check the extracted items, adjust quantities or expiry dates, then add them all at once.',
        placement: 'top',
      },
    ],
    mobile: [
      {
        id: 'camera-receipt',
        target: 'receipt-camera-button',
        title: 'Snap a Receipt',
        content: 'Take a photo of your receipt. Works best with good lighting and a flat surface.',
        placement: 'top',
      },
      {
        id: 'review-items-mobile',
        target: 'receipt-review-list',
        title: 'Quick Review',
        content: 'Swipe to remove items you don\'t want. Tap to edit details. Then hit "Add All".',
        placement: 'center',
      },
    ],
  },
};

/**
 * Households tour shown as part of onboarding.
 * Guides users through creating or joining a household and understanding settings.
 *
 * The tour has two paths:
 * - Path 1 (create): User creates a new household
 * - Path 2 (join): User sees a demo of what joining looks like using ghost data
 *
 * Steps without a `path` property are shown on all paths.
 */
export const HOUSEHOLDS_TOUR: TourDefinition = {
  id: 'households',
  name: 'Households Tour',
  description: 'Set up your household and learn how to manage settings',
  version: 3,
  deviceTypes: ['web', 'mobile'],
  triggerCondition: 'immediate',
  steps: {
    web: [
      // === NAVIGATE TO HOUSEHOLDS ===
      {
        id: 'households-nav',
        target: '[data-tour="households"]',
        title: "Let's Get Started",
        content: "First, let's set up your household. Click here to go to the Households page.",
        placement: 'right',
        navigateTo: '/households',
      },
      // === HOUSEHOLDS PAGE ===
      {
        id: 'households-intro',
        target: '[data-tour="households-page"]',
        title: 'What Are Households?',
        content:
          'A household groups your inventory, storage locations, and members in one place. Most people have one household for their home, but you can create more for a cottage, office, or a family member you help.',
        placement: 'center',
      },
      {
        id: 'join-or-create',
        target: '[data-tour="households-page"]',
        title: 'Join or Create?',
        content: 'Ready to create your own household, or waiting for an invite from someone else?',
        placement: 'center',
        showPathChoice: true,
        canPause: true,
        pauseLabel: "I'm waiting for an invite",
      },
      // === PATH 1: CREATE ===
      {
        id: 'create-household-btn',
        target: '[data-tour="create-household"]',
        title: 'Create a New Household',
        content: 'Click here to create your first household.',
        placement: 'bottom',
        path: 'create',
        isInteractive: true,
        action: 'create_household',
        waitForElement: '[data-tour="household-name-input"]',
      },
      {
        id: 'name-household',
        target: '[data-tour="household-name-input"]',
        title: 'Name Your Household',
        content:
          "You can name your household almost anything\u2014'Home', 'The Smith Family', or whatever works for you. Type a name and click Create.",
        placement: 'bottom',
        path: 'create',
        isInteractive: true,
        action: 'create_household',
        waitForElement: '[data-tour="household-card"]',
      },
      {
        id: 'household-created',
        target: '[data-tour="household-card"]',
        title: 'Your Household Is Ready',
        content:
          "You can explore household settings anytime using the Settings button. For now, let's add your first item.",
        placement: 'bottom',
        path: 'create',
      },
      {
        id: 'tour-complete',
        target: '[data-tour="households-page"]',
        title: 'Next: Your First Item',
        content:
          "Your household is set up. Let's head to inventory and add your first item to start tracking.",
        placement: 'center',
        path: 'create',
        handoffTo: 'inventory-entry',
      },
      // === PATH 2: JOIN (simplified - no demo data) ===
      {
        id: 'join-info',
        target: '[data-tour="households-page"]',
        title: 'How to Join a Household',
        content:
          "Ask your household owner to invite you from their Households page > Settings > Invite. You'll get an email with a link to join.",
        placement: 'center',
        path: 'join',
      },
      {
        id: 'join-waiting',
        target: '[data-tour="households-page"]',
        title: 'Waiting for Your Invite',
        content:
          "This tour will automatically resume once you join a household. If you'd rather not wait, you can always create your own household instead.",
        placement: 'center',
        path: 'join',
        checkHouseholdAndPause: true,
        handoffTo: 'inventory-entry',
      },
    ],
    mobile: [
      // === HOUSEHOLDS INTRO ===
      {
        id: 'households-intro',
        target: 'households-page',
        title: 'What Are Households?',
        content:
          'A household groups your inventory and members in one place. Most people have one for their home.',
        placement: 'center',
        navigateTo: '/households',
      },
      {
        id: 'join-or-create',
        target: 'households-page',
        title: 'Join or Create?',
        content: 'Ready to create your own household, or waiting for an invite from someone else?',
        placement: 'center',
        showPathChoice: true,
        canPause: true,
        pauseLabel: "I'm waiting for an invite",
      },
      // === PATH 1: CREATE ===
      {
        id: 'create-household-btn',
        target: 'create-household-button',
        title: 'Create a New Household',
        content: 'Tap here to create your first household.',
        placement: 'bottom',
        path: 'create',
        isInteractive: true,
        action: 'create_household',
      },
      {
        id: 'name-household',
        target: 'household-name-input',
        title: 'Name Your Household',
        content:
          "Give it any name\u2014'Home', 'The Smiths', or whatever works. Type a name and tap Create.",
        placement: 'top',
        path: 'create',
        isInteractive: true,
        action: 'create_household',
      },
      {
        id: 'household-created',
        target: 'household-card',
        title: 'Your Household Is Ready',
        content: "Great\u2014you're all set. Let's add your first item next.",
        placement: 'bottom',
        path: 'create',
      },
      {
        id: 'tour-complete',
        target: 'households-page',
        title: 'Next: Your First Item',
        content: "Let's head to your pantry and add your first item.",
        placement: 'center',
        path: 'create',
        handoffTo: 'inventory-entry',
      },
      // === PATH 2: JOIN ===
      {
        id: 'join-info',
        target: 'households-page',
        title: 'How to Join a Household',
        content:
          "Ask your household owner to invite you from Settings > Invite. You'll get a notification with a link to join.",
        placement: 'center',
        path: 'join',
      },
      {
        id: 'join-waiting',
        target: 'households-page',
        title: 'Waiting for Your Invite',
        content: "We'll resume once you join a household. You can always create your own instead.",
        placement: 'center',
        path: 'join',
        checkHouseholdAndPause: true,
        handoffTo: 'inventory-entry',
      },
    ],
  },
};

/**
 * Brief handoff tour that introduces the Inventory page and points users to
 * self-guided mini tours.
 */
export const INVENTORY_ENTRY_TOUR: TourDefinition = {
  id: 'inventory-entry',
  name: 'Your First Item',
  description: 'Add your first item to start tracking your pantry',
  version: 2,
  deviceTypes: ['web', 'mobile'],
  triggerCondition: 'immediate',
  triggerRoute: '/items',
  steps: {
    web: [
      {
        id: 'inventory-intro',
        target: '[data-tour="inventory-page-intro"]',
        title: 'Your Inventory',
        content: "This is your inventory. Let's add your first item!",
        placement: 'bottom',
        navigateTo: '/items',
      },
      {
        id: 'add-first-item',
        target: '[data-tour="add-item"]',
        title: 'Add Your First Item',
        content: 'Click here to add an item to your pantry.',
        placement: 'bottom',
        isInteractive: true,
        action: 'add_item',
        waitForElement: '[data-tour="inventory-item-form"]',
      },
      {
        id: 'form-product',
        target: '[data-tour="inventory-form-product"]',
        title: 'Choose a Product',
        content: 'Search for a product, scan a barcode, or type a custom name.',
        placement: 'right',
      },
      {
        id: 'form-expiry',
        target: '[data-tour="inventory-form-expiry"]',
        title: 'Add an Expiry Date',
        content: 'Adding an expiry date helps us alert you before items go bad.',
        placement: 'right',
      },
      {
        id: 'form-submit',
        target: '[data-tour="inventory-form-submit"]',
        title: 'Save Your Item',
        content: 'Click Save to add your first item.',
        placement: 'top',
      },
      {
        id: 'celebration',
        target: '[data-tour="inventory-page-intro"]',
        title: 'All Set',
        content: 'Your first item is tracked. Look for ? buttons on any page for quick mini-tours.',
        placement: 'center',
      },
    ],
    mobile: [
      {
        id: 'inventory-intro',
        target: 'pantry-screen',
        title: 'Your Pantry',
        content: "This is your inventory. Let's add your first item!",
        placement: 'center',
        navigateTo: '/items',
      },
      {
        id: 'add-first-item',
        target: 'add-item-button',
        title: 'Add Your First Item',
        content: 'Tap here to add an item. You can scan a barcode or search by name.',
        placement: 'top',
        isInteractive: true,
        action: 'add_item',
      },
      {
        id: 'scan-or-search',
        target: 'product-search-input',
        title: 'Scan or Search',
        content: 'Use the barcode scanner for speed, or type a product name to search.',
        placement: 'bottom',
      },
      {
        id: 'celebration',
        target: 'pantry-screen',
        title: "You're All Set!",
        content: 'Your first item is tracked. Explore the tabs to discover more features.',
        placement: 'center',
      },
    ],
  },
};

/**
 * Self-guided mini tour for learning how to browse and review inventory.
 */
export const INVENTORY_BASICS_TOUR: TourDefinition = {
  id: 'inventory-basics',
  name: 'Inventory Basics',
  description: 'Search, filter, and review your inventory quickly',
  version: 1,
  deviceTypes: ['web'],
  triggerCondition: 'manual',
  triggerRoute: '/items',
  steps: {
    web: [
      {
        id: 'inventory-search',
        target: '[data-tour="inventory-search"]',
        title: 'Search Items',
        content: 'Use search to quickly find items by product name or brand.',
        placement: 'bottom',
      },
      {
        id: 'inventory-location-filter',
        target: '[data-tour="inventory-location-filter"]',
        title: 'Filter By Location',
        content: 'Narrow results to a specific pantry, fridge, freezer, or other location.',
        placement: 'bottom',
      },
      {
        id: 'inventory-grouped-list',
        target: '[data-tour="inventory-grouped-list"]',
        title: 'Review Inventory',
        content:
          'Items are grouped by storage location so you can scan what is on hand and act quickly when needed.',
        placement: 'top',
      },
      {
        id: 'inventory-basics-replay',
        target: '[data-tour="inventory-help-basics"]',
        title: 'Replay Anytime',
        content: 'Come back to this ? button whenever you want to revisit these basics.',
        placement: 'left',
      },
    ],
  },
};

/**
 * Self-guided mini tour for learning how to add items.
 */
export const INVENTORY_ADDING_TOUR: TourDefinition = {
  id: 'inventory-adding',
  name: 'Adding Items',
  description: 'Learn bulk add and single item entry',
  version: 1,
  deviceTypes: ['web'],
  triggerCondition: 'manual',
  triggerRoute: '/items',
  steps: {
    web: [
      {
        id: 'inventory-bulk-add',
        target: '[data-tour="inventory-bulk-add"]',
        title: 'Start With Bulk Add',
        content:
          'Use Add Multiple when you have several products to enter at once, like after a large grocery trip.',
        placement: 'bottom',
      },
      {
        id: 'inventory-single-add',
        target: '[data-tour="add-item"]',
        title: 'Single Item Flow',
        content: 'Use Add Item for quick one-off updates. Click Add Item now, then press Next.',
        placement: 'bottom',
        isInteractive: true,
        action: 'add_item',
        waitForElement: '[data-tour="inventory-item-form"]',
      },
      {
        id: 'inventory-form-product',
        target: '[data-tour="inventory-form-product"]',
        title: 'Choose A Product',
        content: 'Search for a product, scan a barcode, or enter a custom name.',
        placement: 'right',
      },
      {
        id: 'inventory-form-storage',
        target: '[data-tour="inventory-form-storage"]',
        title: 'Set Storage Location',
        content: 'Pick where the item lives so your list stays organized by location.',
        placement: 'right',
      },
      {
        id: 'inventory-form-expiry',
        target: '[data-tour="inventory-form-expiry"]',
        title: 'Add Expiry (Optional)',
        content: 'Adding expiry dates improves alerts for items that need attention soon.',
        placement: 'right',
      },
      {
        id: 'inventory-form-submit',
        target: '[data-tour="inventory-form-submit"]',
        title: 'Save Item',
        content:
          'Save to add the item. Bulk add uses the same core fields, so the process will feel familiar.',
        placement: 'top',
      },
    ],
  },
};

/**
 * Dashboard discovery tour — auto-prompts when user has items.
 * Contains the dashboard widget steps that were removed from onboarding.
 */
export const DASHBOARD_DISCOVERY_TOUR: TourDefinition = {
  id: 'dashboard-discovery',
  name: 'Dashboard Discovery',
  description: 'Learn what each dashboard section does once you have items',
  version: 1,
  deviceTypes: ['web'],
  triggerCondition: 'on_route',
  triggerRoute: '/dashboard',
  triggerGuard: { minItems: 1 },
  steps: {
    web: [
      {
        id: 'inventory-health',
        target: '[data-tour="inventory-health"]',
        title: 'Inventory Health',
        content:
          'This shows your pantry health at a glance. The ring fills based on how fresh your items are - green is fresh, yellow needs attention soon, red has expired.',
        placement: 'bottom',
      },
      {
        id: 'stats-grid',
        target: '[data-tour="stats-grid"]',
        title: 'At a Glance',
        content: 'Quick view of your total items and how many need attention soon.',
        placement: 'bottom',
      },
      {
        id: 'quick-actions',
        target: '[data-tour="quick-actions"]',
        title: 'Quick Actions',
        content:
          "Fast access to the features you'll use most - add items, browse inventory, start cooking, or organize storage.",
        placement: 'top',
      },
      {
        id: 'needs-attention-list',
        target: '[data-tour="needs-attention-list"]',
        title: 'Needs Attention',
        content: 'Items close to expiring appear here so you never waste food.',
        placement: 'top',
      },
      {
        id: 'expiring-group',
        target: '[data-tour="expiring-group"]',
        title: 'Item Groupings',
        content: 'Items are grouped by urgency - expired, expiring today, or this week.',
        placement: 'right',
      },
      {
        id: 'item-actions',
        target: '[data-tour="item-actions"]',
        title: 'Item Actions',
        content:
          'Use the checkmark to mark an item as used or the X to mark it as wasted, right from this list.',
        placement: 'left',
      },
    ],
  },
};

/**
 * Self-guided mini tour for dashboard basics.
 */
export const DASHBOARD_BASICS_TOUR: TourDefinition = {
  id: 'dashboard-basics',
  name: 'Dashboard Basics',
  description: 'Quick walkthrough of your dashboard essentials',
  version: 1,
  deviceTypes: ['web'],
  triggerCondition: 'manual',
  triggerRoute: '/dashboard',
  steps: {
    web: [
      {
        id: 'dashboard-welcome',
        target: '[data-tour="welcome"]',
        title: 'Dashboard Overview',
        content:
          'This is your snapshot of pantry health. It helps you spot what needs attention quickly.',
        placement: 'bottom',
      },
      {
        id: 'dashboard-inventory-health',
        target: '[data-tour="inventory-health"]',
        title: 'Inventory Health',
        content:
          'This card summarizes freshness so you can prioritize what to use first and reduce waste.',
        placement: 'bottom',
      },
      {
        id: 'dashboard-stats-grid',
        target: '[data-tour="stats-grid"]',
        title: 'Key Stats',
        content: 'These counters help you track total items and urgency at a glance.',
        placement: 'bottom',
      },
      {
        id: 'dashboard-quick-actions',
        target: '[data-tour="quick-actions"]',
        title: 'Quick Actions',
        content:
          'Use these shortcuts to add items, jump to inventory, and manage household workflows.',
        placement: 'top',
      },
      {
        id: 'dashboard-basics-replay',
        target: '[data-tour="dashboard-help-basics"]',
        title: 'Replay Anytime',
        content: 'Use this ? button whenever you want to quickly revisit dashboard basics.',
        placement: 'left',
      },
    ],
  },
};

/**
 * Self-guided mini tour for statistics page basics.
 */
export const STATISTICS_BASICS_TOUR: TourDefinition = {
  id: 'statistics-basics',
  name: 'Statistics Basics',
  description: 'Quick walkthrough of your product insights',
  version: 1,
  deviceTypes: ['web'],
  triggerCondition: 'on_route',
  triggerRoute: '/statistics',
  triggerGuard: { minItems: 3 },
  steps: {
    web: [
      {
        id: 'statistics-page-intro',
        target: '[data-tour="statistics-header"]',
        title: 'Statistics Overview',
        content:
          'This page helps you understand purchase, consumption, waste, and spending trends over time.',
        placement: 'bottom',
      },
      {
        id: 'statistics-summary',
        target: '[data-tour="statistics-summary"]',
        title: 'Summary Snapshot',
        content:
          'This section combines flow and totals so you can quickly spot how efficiently your pantry is being used.',
        placement: 'top',
      },
      {
        id: 'statistics-products-grid',
        target: '[data-tour="statistics-products-grid"]',
        title: 'Product Breakdown',
        content:
          'Each card shows performance for one product, including stock, purchased, consumed, wasted, and waste rate.',
        placement: 'top',
      },
      {
        id: 'statistics-product-details',
        target: '[data-tour="statistics-product-card"]',
        title: 'Open Product Details',
        content:
          'Select a product card to open deeper stats, including reason breakdowns and trend context.',
        placement: 'top',
      },
      {
        id: 'statistics-basics-replay',
        target: '[data-tour="statistics-help"]',
        title: 'Replay Anytime',
        content: 'Use Help whenever you want to revisit statistics basics.',
        placement: 'left',
      },
    ],
  },
};

/**
 * Self-guided mini tour for statistics filters and controls.
 */
export const STATISTICS_CONTROLS_TOUR: TourDefinition = {
  id: 'statistics-controls',
  name: 'Statistics Controls',
  description: 'Learn timeframe and sorting controls',
  version: 1,
  deviceTypes: ['web'],
  triggerCondition: 'manual',
  triggerRoute: '/statistics',
  steps: {
    web: [
      {
        id: 'statistics-filters',
        target: '[data-tour="statistics-filters"]',
        title: 'Filters Bar',
        content: 'Use these controls to adjust which activity the page summarizes.',
        placement: 'bottom',
      },
      {
        id: 'statistics-timeframe',
        target: '[data-tour="statistics-timeframe"]',
        title: 'Timeframe',
        content:
          'Switch between all-time, in-stock, or custom range to answer different inventory questions.',
        placement: 'bottom',
      },
      {
        id: 'statistics-sort',
        target: '[data-tour="statistics-sort"]',
        title: 'Sort & Direction',
        content:
          'Sort by purchased, consumed, wasted, spending, or waste rate and flip direction for quick analysis.',
        placement: 'left',
      },
      {
        id: 'statistics-product-card-controls',
        target: '[data-tour="statistics-product-card"]',
        title: 'Inspect Specific Products',
        content:
          'After filtering and sorting, open a product card to validate why a result is high or low.',
        placement: 'top',
      },
      {
        id: 'statistics-controls-replay',
        target: '[data-tour="statistics-help"]',
        title: 'Replay Anytime',
        content: 'Use Help to rerun this controls walkthrough whenever you need a refresher.',
        placement: 'left',
      },
    ],
  },
};

/**
 * Self-guided mini tour for settings essentials.
 */
export const SETTINGS_BASICS_TOUR: TourDefinition = {
  id: 'settings-basics',
  name: 'Settings Tour',
  description: 'Guided walkthrough of all key settings sections',
  version: 1,
  deviceTypes: ['web'],
  triggerCondition: 'manual',
  triggerRoute: '/settings',
  steps: {
    web: [
      {
        id: 'settings-page-intro',
        target: '[data-tour="settings-header"]',
        title: 'Settings Overview',
        content:
          'This page is where you manage profile, preferences, notifications, and account controls.',
        placement: 'bottom',
      },
      {
        id: 'settings-profile',
        target: '[data-tour="settings-profile"]',
        title: 'Profile',
        content: 'Update your display name and avatar so your household members can recognize you.',
        placement: 'bottom',
      },
      {
        id: 'settings-security',
        target: '[data-tour="settings-security"]',
        title: 'Security',
        content: 'Use this section to change your password and keep your account secure.',
        placement: 'bottom',
      },
      {
        id: 'settings-email',
        target: '[data-tour="settings-email"]',
        title: 'Email Address',
        content: 'Update your account email and complete the confirmation flow from both inboxes.',
        placement: 'bottom',
      },
      {
        id: 'settings-linked-accounts',
        target: '[data-tour="settings-linked-accounts"]',
        title: 'Linked Accounts',
        content: 'Connect or unlink sign-in providers like Apple or Google for easier access.',
        placement: 'bottom',
      },
      {
        id: 'settings-preferences-section',
        target: '[data-tour="settings-preferences"]',
        title: 'Preferences',
        content: 'Choose measurement system and language defaults for your app experience.',
        placement: 'bottom',
      },
      {
        id: 'settings-notifications',
        target: '[data-tour="settings-notifications"]',
        title: 'Notifications',
        content:
          'Configure reminders, quiet hours, and timing so alerts are useful without being noisy.',
        placement: 'top',
      },
      {
        id: 'settings-tours',
        target: '[data-tour="settings-tours"]',
        title: 'Tours Library',
        content:
          'Use this section to start or restart guided tours whenever you want a quick feature refresher.',
        placement: 'top',
      },
      {
        id: 'settings-danger-zone',
        target: '[data-tour="settings-danger-zone"]',
        title: 'Danger Zone',
        content:
          'This area contains irreversible account actions. Review carefully before making changes.',
        placement: 'top',
      },
      {
        id: 'settings-basics-replay',
        target: '[data-tour="settings-help"]',
        title: 'Replay Anytime',
        content: 'Use Help whenever you want to revisit settings basics.',
        placement: 'left',
      },
    ],
  },
};

/**
 * Self-guided mini tour for household setup basics.
 */
export const HOUSEHOLDS_BASICS_TOUR: TourDefinition = {
  id: 'households-basics',
  name: 'Households Basics',
  description: 'Quick walkthrough of the households page',
  version: 1,
  deviceTypes: ['web'],
  triggerCondition: 'manual',
  triggerRoute: '/households',
  steps: {
    web: [
      {
        id: 'households-page-intro',
        target: '[data-tour="households-page"]',
        title: 'Households Page',
        content:
          'This page is where you create and manage households for shared inventory collaboration.',
        placement: 'center',
      },
      {
        id: 'households-create-action',
        target: '[data-tour="create-household"]',
        title: 'Create Household',
        content:
          'Use this button to create a household, then invite family members to collaborate together.',
        placement: 'bottom',
      },
      {
        id: 'households-basics-replay',
        target: '[data-tour="households-help-basics"]',
        title: 'Replay Anytime',
        content: 'Use this ? button whenever you want a quick households refresher.',
        placement: 'left',
      },
    ],
  },
};

/**
 * Self-guided mini tour for household settings page basics.
 */
export const HOUSEHOLDS_SETTINGS_BASICS_TOUR: TourDefinition = {
  id: 'households-settings-basics',
  name: 'Household Settings Basics',
  description: 'Quick walkthrough of key settings in a household',
  version: 1,
  deviceTypes: ['web'],
  triggerCondition: 'manual',
  triggerRoute: '/households',
  steps: {
    web: [
      {
        id: 'household-settings-page-intro',
        target: '[data-tour="household-settings-page"]',
        title: 'Settings Overview',
        content: 'This page controls how your household is configured for all members who use it.',
        placement: 'center',
        navigateTo: 'firstHouseholdSettings',
      },
      {
        id: 'household-settings-name-mini',
        target: '[data-tour="settings-name"]',
        title: 'Household Name',
        content: 'Update the household name so everyone recognizes it easily.',
        placement: 'bottom',
      },
      {
        id: 'household-settings-region-mini',
        target: '[data-tour="settings-region"]',
        title: 'Region',
        content:
          'Region improves product results and defaults for this household across supported features.',
        placement: 'bottom',
      },
      {
        id: 'household-settings-language-mini',
        target: '[data-tour="settings-language"]',
        title: 'Default Language',
        content:
          'Set a default product-name language for the household. Members can still override personally.',
        placement: 'bottom',
      },
      {
        id: 'household-settings-members-mini',
        target: '[data-tour="members-section"]',
        title: 'Members',
        content:
          'Review members and roles here. Owners and admins can invite and manage household access.',
        placement: 'top',
      },
      {
        id: 'household-settings-basics-replay',
        target: '[data-tour="households-settings-help-basics"]',
        title: 'Replay Anytime',
        content: 'Use this ? button whenever you want to revisit settings basics.',
        placement: 'left',
      },
    ],
  },
};

/**
 * Self-guided mini tour for meal prep basics.
 */
export const MEAL_PREP_BASICS_TOUR: TourDefinition = {
  id: 'meal-prep-basics',
  name: 'Meal Prep Basics',
  description: 'Learn how to select items for meal prep',
  version: 1,
  deviceTypes: ['web'],
  triggerCondition: 'on_route',
  triggerRoute: '/meal-prep',
  triggerGuard: { minItems: 1 },
  steps: {
    web: [
      {
        id: 'meal-prep-page-intro',
        target: '[data-tour="meal-prep-page-intro"]',
        title: 'Meal Prep Workflow',
        content:
          'Use this page to quickly process multiple items after cooking by selecting what you used or wasted.',
        placement: 'bottom',
      },
      {
        id: 'meal-prep-quick-actions',
        target: '[data-tour="meal-prep-quick-actions"]',
        title: 'Add Items To The Session',
        content:
          'Start by scanning barcodes or picking from inventory. Items you select are added to the session below.',
        placement: 'bottom',
      },
      {
        id: 'meal-prep-scanner',
        target: '[data-tour="meal-prep-open-scanner"]',
        title: 'Scan Barcodes',
        content:
          'Scanner mode is fastest during meal prep. It can add known items directly from inventory.',
        placement: 'bottom',
      },
      {
        id: 'meal-prep-picker',
        target: '[data-tour="meal-prep-pick-list"]',
        title: 'Pick From Inventory',
        content: 'Use the list picker when you prefer browsing your pantry instead of scanning.',
        placement: 'bottom',
      },
      {
        id: 'meal-prep-selected-items',
        target: '[data-tour="meal-prep-selected-items"]',
        title: 'Selection Area',
        content: 'Selected items appear here for review before you confirm the final action.',
        placement: 'top',
      },
      {
        id: 'meal-prep-basics-replay',
        target: '[data-tour="meal-prep-help"]',
        title: 'Replay Anytime',
        content: 'Use this ? button whenever you want to quickly revisit meal prep basics.',
        placement: 'left',
      },
    ],
  },
};

/**
 * Self-guided mini tour for meal prep actions.
 */
export const MEAL_PREP_ACTIONS_TOUR: TourDefinition = {
  id: 'meal-prep-actions',
  name: 'Meal Prep Actions',
  description: 'Understand used vs wasted flow before confirming',
  version: 1,
  deviceTypes: ['web'],
  triggerCondition: 'manual',
  triggerRoute: '/meal-prep',
  steps: {
    web: [
      {
        id: 'meal-prep-action-bar',
        target: '[data-tour="meal-prep-action-bar"]',
        title: 'Action Bar',
        content: 'Once items are selected, use this bar to batch process them as used or wasted.',
        placement: 'top',
      },
      {
        id: 'meal-prep-used-action',
        target: '[data-tour="meal-prep-used-button"]',
        title: 'Mark As Used',
        content:
          'Choose Used when items were cooked, eaten, shared, or donated. You can adjust quantities in the next step.',
        placement: 'top',
      },
      {
        id: 'meal-prep-wasted-action',
        target: '[data-tour="meal-prep-wasted-button"]',
        title: 'Mark As Wasted',
        content:
          'Choose Wasted for spoilage, expiry, or damage. You will confirm quantities and a reason before submitting.',
        placement: 'top',
      },
      {
        id: 'meal-prep-actions-replay',
        target: '[data-tour="meal-prep-help"]',
        title: 'Replay Anytime',
        content: 'Use this ? button whenever you want a quick reminder of meal prep actions.',
        placement: 'left',
      },
    ],
  },
};

/**
 * Shopping lists tour shown when user first accesses shopping lists.
 */
export const SHOPPING_LISTS_TOUR: TourDefinition = {
  id: 'shopping-lists',
  name: 'Shopping Lists Tour',
  description: 'Learn how to create and share shopping lists',
  version: 1,
  deviceTypes: ['web', 'mobile'],
  triggerCondition: 'on_route',
  triggerRoute: '/shopping-lists',
  steps: {
    web: [
      {
        id: 'create-list',
        target: '[data-tour="create-shopping-list"]',
        title: 'Create a List',
        content: 'Start a new shopping list for your next grocery run.',
        placement: 'bottom',
      },
      {
        id: 'add-from-expiring',
        target: '[data-tour="add-from-expiring"]',
        title: 'Smart Suggestions',
        content: "We'll suggest items based on what you're running low on or frequently buy.",
        placement: 'right',
        missingElementContent:
          'Once you have items in your pantry, we can suggest what to add to your shopping list.',
      },
      {
        id: 'share-list',
        target: '[data-tour="share-list"]',
        title: 'Share with Household',
        content: 'Share this list with household members. Changes sync in real-time.',
        placement: 'left',
      },
    ],
    mobile: [
      {
        id: 'shopping-tab',
        target: 'shopping-tab',
        title: 'Shopping Lists',
        content: 'All your shopping lists live here. Tap to create a new one.',
        placement: 'top',
      },
      {
        id: 'check-items',
        target: 'shopping-item-row',
        title: 'Check Off Items',
        content: 'Tap items as you shop. Checked items move to the bottom automatically.',
        placement: 'center',
        missingElementContent: 'Add items to your list, then tap to check them off as you shop.',
      },
    ],
  },
};

/**
 * All available tours in the application.
 */
export const TOURS: TourDefinition[] = [
  ONBOARDING_TOUR,
  HOUSEHOLDS_TOUR,
  INVENTORY_ENTRY_TOUR,
  INVENTORY_BASICS_TOUR,
  INVENTORY_ADDING_TOUR,
  DASHBOARD_DISCOVERY_TOUR,
  DASHBOARD_BASICS_TOUR,
  STATISTICS_BASICS_TOUR,
  STATISTICS_CONTROLS_TOUR,
  SETTINGS_BASICS_TOUR,
  HOUSEHOLDS_BASICS_TOUR,
  HOUSEHOLDS_SETTINGS_BASICS_TOUR,
  MEAL_PREP_BASICS_TOUR,
  MEAL_PREP_ACTIONS_TOUR,
  RECEIPT_SCANNING_TOUR,
  SHOPPING_LISTS_TOUR,
];

/**
 * Get a tour definition by ID.
 */
export function getTourById(tourId: string): TourDefinition | undefined {
  return TOURS.find((tour) => tour.id === tourId);
}

/**
 * Get all tour IDs.
 */
export function getTourIds(): string[] {
  return TOURS.map((tour) => tour.id);
}
